import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Col } from 'react-bootstrap';

import { useAppSelector } from 'hooks';
import { LastMeasurement } from 'components/UI';
import { CalendarIcon } from 'assets/icons';
import { getAlertClassSvg } from 'utils/Icons';
import { formatRecurrence } from 'utils/Time';
import { DropdownGroup } from 'components/Forms';

import ActionsTask from '../../../ActionsTask';
import DeleteObservationModal from './DeleteObservationModal';

export const ObservationContent = ({
  observation,
  dataTestId
}: {
  observation: ICarePlanObservationTask;
  dataTestId?: string;
}) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);

  return (
    <div className="p-3 d-flex align-items-center bg-light">
      <div data-test={`${dataTestId}-icon`} className="observationIconClass">
        <img src={getAlertClassSvg(observation.mClass)} />
      </div>
      <div className="w-100">
        <div
          data-test={`${dataTestId}-class`}
          data-test-value={observation.mClass}
          className="font-weight-bolder text-sm mb-2"
        >
          {intl.formatMessage({ id: observation.title })}
        </div>
        <div
          data-test={`${dataTestId}-last-reading`}
          className="text-sm mb-0 d-flex align-items-center"
        >
          <div className="me-2">{intl.formatMessage({ id: 'lastReadingKey' })}:</div>
          {observation.lastMeasurement ? (
            <LastMeasurement record={observation.lastMeasurement} />
          ) : (
            '-'
          )}
        </div>
        <div data-test={`${dataTestId}-recurrence`} className="text-sm font-weight-bolder mt-2">
          <CalendarIcon className="icon me-2" style={{ fontSize: '14px' }} />
          {!observation.isScheduled
            ? intl.formatMessage({ id: 'unscheduledKey' })
            : observation.recurrence_hr &&
              formatRecurrence(
                observation.recurrence_hr,
                locale,
                intl.formatMessage({ id: 'atKey' })
              )}
        </div>
        {observation.action.survey?.actions_on_task &&
          observation.action.survey?.actions_on_task.length > 0 && (
            <ActionsTask
              dataTestId={dataTestId}
              tasks={observation.action.survey?.actions_on_task}
            />
          )}
      </div>
    </div>
  );
};

interface IObservationContainerProps {
  observation: ICarePlanObservationTask;
  onDeleteObservation: (id: string) => any;
  carePlanTemplate?: ICarePlanTemplate;
  dataTestId: string;
  onShowEditModal: () => void;
  disabled?: boolean;
}

const Observation = ({
  dataTestId,
  observation,
  onDeleteObservation,
  carePlanTemplate,
  onShowEditModal,
  disabled
}: IObservationContainerProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';

  const handleDeleteObservation = () => {
    setShowDeleteModal(false);
    onDeleteObservation(observation.id);
  };

  return (
    <Col data-test={dataTestId} xl={4} lg={6} className="p-3">
      <div className="bg-light">
        {!isTemplateGlobal && (
          <DropdownGroup
            dataTestId={dataTestId}
            onShowEditModal={onShowEditModal}
            onShowDeleteModal={() => setShowDeleteModal(true)}
            disabled={disabled}
          />
        )}
        <ObservationContent observation={observation} dataTestId={dataTestId} />
      </div>

      <DeleteObservationModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        observation={observation}
        onDeleteObservation={handleDeleteObservation}
        carePlanTemplate={carePlanTemplate}
      />
    </Col>
  );
};

export default Observation;
